import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Briefcase,
  Users,
  UserPlus,
  BookUser,
  Settings,
  LogOut,
  Menu,
  FileText,
  Clock,
  TrendingUp,
  ChevronDown,
  CalendarDays,
  BarChart3,
  ClipboardEdit,
  Pencil,
  PanelLeftClose,
  PanelLeft,
  Award,
  Receipt,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import NotificationBell from "./NotificationBell";
import FollowUpBadge from "./FollowUpBadge";
import MailHealthBanner from "./MailHealthBanner";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar
  const [reportsOpen, setReportsOpen] = useState(false);
  
  // Sidebar collapsed state - persisted per user
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (!user?.id) return false;
    const saved = localStorage.getItem(`sidebar_collapsed_${user.id}`);
    return saved === "true";
  });

  // Save collapsed state when it changes
  // Wheel/trackpad over the sidebar must never scroll the page: swallow it unless the nav itself can scroll that way.
  const asideRef = useRef(null);
  const navRef = useRef(null);
  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return undefined;
    const onWheel = (e) => {
      const nav = navRef.current;
      const canScroll = nav && nav.scrollHeight > nav.clientHeight && nav.contains(e.target);
      if (!canScroll) { e.preventDefault(); return; }
      const atTop = nav.scrollTop <= 0 && e.deltaY < 0;
      const atBottom = Math.ceil(nav.scrollTop + nav.clientHeight) >= nav.scrollHeight && e.deltaY > 0;
      if (atTop || atBottom) e.preventDefault();
    };
    aside.addEventListener("wheel", onWheel, { passive: false });
    return () => aside.removeEventListener("wheel", onWheel);
  }, []);

  // Fixed page headers: measure the current page's `.page-header` and reserve that height at the top of <main>.
  const mainRef = useRef(null);
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return undefined;
    let current; // undefined until the first attach() so pages without a header still get classed
    let ro = null;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!current) return;
        const h = current.offsetHeight;
        if (h > 0) main.style.setProperty("--page-header-h", `${h}px`); else main.style.removeProperty("--page-header-h");
        const title = current.querySelector("h1");
        if (title) title.classList.toggle("title-clipped", title.scrollWidth > title.clientWidth + 1);
      });
    };
    const attach = () => {
      const candidates = [...main.querySelectorAll(".page-header, [data-page-header]")];
      const next = candidates.find((el) => el.offsetHeight > 0) || candidates[0] || null;
      if (next === current) return;
      ro?.disconnect();
      current = next;
      main.parentElement?.classList.toggle("dark-page-header", current?.dataset.pageHeader === "dark");
      main.parentElement?.classList.toggle("no-page-header", !current);
      if (!current) { main.style.removeProperty("--page-header-h"); return; }
      ro = new ResizeObserver(measure);
      ro.observe(current);
      measure();
    };
    attach();
    const mo = new MutationObserver(attach);
    mo.observe(main, { childList: true, subtree: true });
    const onResize = () => { current = undefined; attach(); };
    window.addEventListener("resize", onResize);
    return () => { mo.disconnect(); ro?.disconnect(); cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);

  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`sidebar_collapsed_${user.id}`, sidebarCollapsed.toString());
    }
  }, [sidebarCollapsed, user?.id]);

  // Load collapsed state when user changes
  useEffect(() => {
    if (user?.id) {
      const saved = localStorage.getItem(`sidebar_collapsed_${user.id}`);
      setSidebarCollapsed(saved === "true");
    }
  }, [user?.id]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const isAdmin = user?.role === "admin";
  const isProjectManager = user?.role === "project_manager";
  const isOfficeManager = user?.role === "office_manager";
  const canAccessReports = isAdmin || isProjectManager || isOfficeManager;
  const canAccessAnalytics = isAdmin || isProjectManager;

  // On mobile, always show expanded sidebar when open (ignore desktop collapsed state)
  const isEffectivelyCollapsed = sidebarCollapsed && !sidebarOpen;

  // Sidebar width based on collapsed state
  const sidebarWidth = isEffectivelyCollapsed ? "w-16" : "w-56";
  const mainMargin = isEffectivelyCollapsed ? "md:ml-16" : "md:ml-56";

  // NavLink component with tooltip for collapsed state
  const SidebarLink = ({ to, icon: Icon, label, badge, end, testId, onClick }) => {
    const collapsedStyle = isEffectivelyCollapsed ? {
      width: '44px',
      height: '44px',
      minHeight: '44px',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      gap: '0'
    } : {};
    
    const link = (
      <NavLink
        to={to}
        end={end}
        onClick={() => {
          setSidebarOpen(false);
          onClick?.();
        }}
        style={collapsedStyle}
        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} ${isEffectivelyCollapsed ? "rounded-md" : ""}`}
        data-testid={testId}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {!isEffectivelyCollapsed && <span>{label}</span>}
        {!isEffectivelyCollapsed && badge}
      </NavLink>
    );

    if (isEffectivelyCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {link}
          </TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            {label}
            {badge}
          </TooltipContent>
        </Tooltip>
      );
    }

    return link;
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[55] md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Fixed position */}
        <aside
          ref={asideRef}
          className={`fixed inset-y-0 left-0 z-[60] ${sidebarWidth} bg-card border-r border-border transform transition-all duration-300 md:translate-x-0 ${
            sidebarOpen ? "translate-x-0 w-56" : "-translate-x-full md:translate-x-0"
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className={`relative z-10 flex items-center gap-3 px-4 pt-5 pb-5 ${isEffectivelyCollapsed ? "justify-center" : ""}`} style={{ background: "linear-gradient(to bottom, hsl(var(--card)) 0%, hsl(var(--card)) calc(100% - 1rem), hsl(var(--card) / 0) 100%)" }}>
              <img src="/logo.png" alt="V2R Masonry Systems" className="w-12 h-12 rounded-sm object-contain flex-shrink-0" />
              {!isEffectivelyCollapsed && (
                <h1 className="text-xl font-bold text-foreground tracking-tight leading-tight">V2R Masonry<br />Systems</h1>
              )}
            </div>

            {/* Navigation */}
            <nav ref={navRef} className={`flex-1 -mt-4 pt-4 pb-4 overflow-y-auto overscroll-contain flex flex-col ${isEffectivelyCollapsed ? "px-1 gap-2" : "px-3 space-y-1"}`}>
              <SidebarLink
                to="/dashboard"
                icon={LayoutDashboard}
                label="Dashboard"
                testId="nav-dashboard"
              />

              <SidebarLink
                to="/jobs"
                icon={Briefcase}
                label="Jobs"
                testId="nav-jobs"
              />

              <SidebarLink
                to="/daily-entry"
                icon={ClipboardEdit}
                label="Daily Entry"
                testId="nav-daily-entry"
              />

              <SidebarLink
                to="/receipts"
                icon={Receipt}
                label="Receipts"
                testId="nav-receipts"
              />

              {/* Reports Section */}
              {isEffectivelyCollapsed ? (
                // When collapsed, show just an icon that expands to a dropdown or links directly
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to="/reports/hours"
                      onClick={() => setSidebarOpen(false)}
                      style={{ width: '44px', height: '44px', minHeight: '44px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', gap: '0' }}
                      className={({ isActive }) => `sidebar-link rounded-md ${isActive ? "active" : ""}`}
                      data-testid="nav-reports-collapsed"
                    >
                      <FileText className="w-5 h-5" />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Reports
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Collapsible open={reportsOpen} onOpenChange={setReportsOpen}>
                  <CollapsibleTrigger className="sidebar-link w-full justify-between" data-testid="nav-reports-toggle">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5" />
                      <span>Reports</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${reportsOpen ? "rotate-180" : ""}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-1 mt-1">
                    <NavLink
                      to="/reports/hours"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                      data-testid="nav-hours-report"
                    >
                      <Clock className="w-5 h-5" />
                      <span>Hours</span>
                    </NavLink>
                    {canAccessReports && (
                      <NavLink
                        to="/reports/daily"
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                        data-testid="nav-daily-report"
                      >
                        <CalendarDays className="w-5 h-5" />
                        <span>Daily Report</span>
                      </NavLink>
                    )}
                    {canAccessReports && (
                      <NavLink
                        to="/reports/training"
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                        data-testid="nav-training-tracking"
                      >
                        <Award className="w-5 h-5" />
                        <span>Training Tracking</span>
                      </NavLink>
                    )}
                    {canAccessReports && (
                      <NavLink
                        to="/reports/productivity"
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                        data-testid="nav-productivity"
                      >
                        <TrendingUp className="w-5 h-5" />
                        <span>Productivity</span>
                      </NavLink>
                    )}
                    {canAccessReports && (
                      <NavLink
                        to="/reports"
                        end
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                        data-testid="nav-profit-loss"
                      >
                        <TrendingUp className="w-5 h-5" />
                        <span>Profit/Loss</span>
                      </NavLink>
                    )}
                    {canAccessAnalytics && (
                      <NavLink
                        to="/analytics"
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                        data-testid="nav-analytics"
                      >
                        <BarChart3 className="w-5 h-5" />
                        <span>Analytics</span>
                      </NavLink>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              )}

              <SidebarLink
                to="/employees"
                icon={Users}
                label="Employees"
                testId="nav-employees"
              />

              {(isAdmin || isProjectManager || isOfficeManager) && (
                <SidebarLink
                  to="/applications"
                  icon={UserPlus}
                  label="Applications"
                  testId="nav-applications"
                />
              )}

              {(isAdmin || isProjectManager || isOfficeManager) && (
                <SidebarLink
                  to="/contacts"
                  icon={BookUser}
                  label="Contacts"
                  testId="nav-contacts"
                />
              )}

              {canAccessReports && (
                <SidebarLink
                  to="/settings"
                  icon={Settings}
                  label="Settings"
                  testId="nav-settings"
                />
              )}
            </nav>

            {/* Collapse toggle button - Desktop only */}
            <div className="hidden md:flex px-2 py-2 border-t border-border">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`w-full ${isEffectivelyCollapsed ? "justify-center px-0" : "justify-start gap-3"}`}
                    onClick={toggleSidebarCollapse}
                    data-testid="sidebar-collapse-btn"
                  >
                    {isEffectivelyCollapsed ? (
                      <PanelLeft className="w-5 h-5" />
                    ) : (
                      <>
                        <PanelLeftClose className="w-5 h-5" />
                        <span>Collapse</span>
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                {isEffectivelyCollapsed && (
                  <TooltipContent side="right">
                    Expand sidebar
                  </TooltipContent>
                )}
              </Tooltip>
            </div>

            {/* User info & logout */}
            <div className={`p-2 border-t border-border ${isEffectivelyCollapsed ? "px-2" : "p-4"}`}>
              {isEffectivelyCollapsed ? (
                // Collapsed user section
                <div className="flex flex-col items-center gap-2">
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to="/profile"
                        onClick={() => setSidebarOpen(false)}
                        className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-primary/10 transition-colors"
                        data-testid="nav-profile"
                      >
                        <span className="text-sm font-medium text-muted-foreground">
                          {user?.name?.charAt(0).toUpperCase()}
                        </span>
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <div>
                        <p className="font-medium">{user?.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive/60 hover:text-destructive hover:bg-destructive/10"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-5 h-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Logout</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                // Expanded user section
                <>
                  <NavLink
                    to="/profile"
                    onClick={() => setSidebarOpen(false)}
                    className="group flex items-center gap-3 px-3 py-2 mb-2 rounded-md hover:bg-muted transition-colors"
                    data-testid="nav-profile"
                  >
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground leading-tight">{user?.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
                    </div>
                    <Pencil className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </NavLink>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Main content - with left margin to account for fixed sidebar */}
        <div className={`${mainMargin} flex flex-col min-h-screen transition-all duration-300`} style={{ "--sidebar-w": isEffectivelyCollapsed ? "4rem" : "14rem" }}>
          {/* Floating controls (no header bar) */}
          <Button
            variant="ghost"
            size="icon"
            className="mobile-menu-btn md:hidden fixed top-3 left-3 z-30 bg-background/80 backdrop-blur border border-border shadow-sm"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="top-fade" aria-hidden="true" />
          <div className="floating-header-actions fixed top-3 right-3 md:top-4 md:right-6 z-30 flex items-center gap-2" data-testid="floating-header-actions">
            <FollowUpBadge />
            <NotificationBell />
          </div>
          <MailHealthBanner />

          {/* Page content */}
          <main ref={mainRef} className="flex-1 min-w-0 overflow-x-clip p-4 md:p-6 lg:p-8" style={{ paddingTop: "var(--page-header-h, 4rem)" }}>
            <Outlet />
            <div className="h-16" aria-hidden="true" />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Layout;
